import { keyBy, mapValues } from 'lodash';
import { maybe, Maybe, rd } from '@passionware/monads';
import {
  appScopeUtils,
  type AppScopeIdentity
} from 'v5/core/app-scope/appScope.types';
import { FeatureFlagSet } from 'v5/api/enterprise-feature-flag/enterprise-feature-flag.api';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import { FeatureFlagSource } from 'v5/platform/feature-management/feature-management';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { EnterpriseFeatureFlagService } from 'v5/services/io/EnterpriseFeatureFlagService/EnterpriseFeatureFlagService';

export function selectNamesForScope(
  set: FeatureFlagSet,
  scope: AppScopeIdentity
): string[] {
  if (scope.level === 'organization') {
    return set.legacy;
  }

  const workspaceId = appScopeUtils.getWorkspaceId(scope);
  if (maybe.isAbsent(workspaceId)) {
    return [];
  }

  return set.byWorkspace[workspaceId] ?? [];
}

function namesToValues(names: string[]) {
  return mapValues(keyBy(names), () => true as const);
}

export function createServerFeatureFlagSource(config: {
  enterpriseFeatureFlagService: EnterpriseFeatureFlagService;
}): FeatureFlagSource<AtellioFeatureGroupDefinition, Maybe<AppScopeIdentity>> {
  const { enterpriseFeatureFlagService } = config;

  return {
    useFeatureFlags: scope => {
      const flagSetRd = config.enterpriseFeatureFlagService.useFlagSet(
        maybe.map(scope, item => item.organizationId)
      );
      const mapped = rd.useMemoMap(
        flagSetRd,
        (set, flagScope) =>
          namesToValues(selectNamesForScope(set, maybe.getOrThrow(flagScope))),
        scope
      );
      return ensureIdleQuery(scope, mapped);
    },
    getFeatureFlags: async scope => {
      if (maybe.isAbsent(scope)) {
        throw new Error('Scope is required to read feature flags');
      }
      const set = await enterpriseFeatureFlagService.ensureFlagSet(
        scope.organizationId
      );
      return namesToValues(selectNamesForScope(set, scope));
    }
  };
}
